/**
 * Who is signed in on this device, and the one place that decides it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE MODEL, WHICH IS NOT NEGOTIABLE ANYWHERE IN THIS FILE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **Passwordless, and no self-registration** (spec 09 §1, decision D-3). The users are
 *     the TD and six Umpires Managers. There is no sign-up path, no password field and no
 *     account recovery — because there is no account to recover, only a link to re-issue.
 *
 *  2. **This application never sends email** (D19/D20). The Control Centre generates a link
 *     and displays it for the TD to copy; the TD sends it from his own mailbox. So spec 09
 *     §3.1's `Send Me A Sign-In Link` button and §3.3's `Send Me A New Link` control are
 *     **not built**, and their absence is stated in the copy rather than left as a dead end.
 *     The deviation is recorded in the run report.
 *
 *  3. **The credential is never in this module's state.** It goes from the redeem response
 *     into IndexedDB (never `localStorage` — `10-data-model.md`, and the API's own note on
 *     `POST /auth/redeem`) and is read back only on its way into an `Authorization` header.
 *     What lives here is the *identity* — a short name and a kind — which is not a secret.
 *
 *     Because that store is asynchronous, **boot has two steps and their order is the whole
 *     point**: `loadCredential()` first, so the credential is in the client's cache, and only
 *     then `GET /auth/me`. An identity call made before the load would go out bare and come
 *     back 401, and this device would sign itself out holding a perfectly good credential.
 *
 *  4. **One dead credential produces one signed-out screen.** Every authenticated 401 is
 *     announced once by `onUnauthorised`; this holder clears the credential and flips to
 *     signed-out, so the panels behind it stop rather than each inventing its own error.
 *
 *     **And only the credential that 401 was answering** (security QA, pass 2, 18 September
 *     2026). The client announces a 401 only while the credential the request carried is still
 *     the one stored. A late 401 for a credential since replaced — the boot check's, for a dead
 *     credential, landing after a link was redeemed on top of it — used to reach this holder and
 *     wipe the credential the link had just stored, signing the device out as expired.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  ApiError,
  clearCredential,
  fetchIdentity,
  loadCredential,
  onUnauthorised,
} from "../api/client.js";
import type { AppIdentityView, TournamentBrandingView } from "../api/types.js";

/** Why the signed-out screen is showing. Each has its own opening line and nothing else. */
export type SignedOutReason =
  /** No credential has ever been stored on this device, or the link was never opened here. */
  | "no_credential"
  /** A credential was held and the API has refused it — lapsed, or revoked by the TD. */
  | "expired"
  /** The person pressed Sign Out. */
  | "signed_out";

export type Session =
  /**
   * Boot. The credential is being read out of IndexedDB and, if one is held, checked against
   * `GET /auth/me`. A device holding nothing passes through this state briefly and asks the
   * API nothing at all — there is no request worth making that can only 401.
   *
   * `credentialHeld` (RUN 87A) is set once the read has found a credential and the check is out:
   * the sign-in screen can then tell a device it is already signed in without waiting on the
   * network for a name. It says nothing about whether that credential still works - the check
   * decides that - and it never carries the credential itself.
   */
  | { status: "checking"; credentialHeld?: true }
  /**
   * `tournament` is WHICH EVENT this installation is running, carried beside the identity
   * because both auth reads return it together (`api/types.ts`). It is here rather than in a
   * fetch of its own so that the chrome has the name at the moment it is first drawn: the
   * shell renders as soon as this status flips, and a separately fetched name would arrive
   * after it.
   *
   * `null` means no tournament is set up on this installation. Every reader draws nothing
   * for it — never a placeholder, and never a remembered previous name.
   */
  | { status: "signed-in"; identity: AppIdentityView; tournament: TournamentBrandingView | null }
  | { status: "signed-out"; reason: SignedOutReason }
  /**
   * The credential could not be checked because the API could not be reached. Deliberately
   * NOT signed-out: a venue wifi drop must not throw away a working credential and leave the
   * TD unable to sign back in without finding him a fresh link.
   */
  | { status: "unreachable"; error: Error };

export interface SessionValue {
  session: Session;
  /**
   * Called by the redeem screen once the credential is stored — with the whole of what the
   * redeem response said about this device: who it belongs to, and which tournament it has
   * just joined.
   */
  signIn: (identity: AppIdentityView, tournament: TournamentBrandingView | null) => void;
  /** Sign-out. Clears the credential; the screen behind it is gone on the next render. */
  signOut: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error("useSession was called outside a SessionProvider.");
  }
  return value;
}

/**
 * WHICH TOURNAMENT this installation is running, for the chrome that names it.
 *
 * `null` while the session is still booting and `null` when no tournament is set up, and the
 * callers treat both the same way: they draw nothing. That is deliberate rather than lazy —
 * the two states differ in cause and not in what may honestly be printed, and a surface that
 * distinguished them would be drawing a spinner in the middle of a masthead for the fraction
 * of a second before `GET /auth/me` returns.
 *
 * **It reads the context directly instead of going through {@link useSession}, and therefore
 * does NOT throw outside a provider.** That is not a relaxation of the rule above it: the
 * rule protects `signIn` and `signOut`, where a missing provider means a credential going
 * nowhere and has to fail loudly. This returns a NAME to print. A panel rendered on its own —
 * in a component test, or in any tree that has no session — genuinely does not know which
 * tournament it belongs to, and `null` is the accurate answer rather than a crash.
 */
export function useTournament(): TournamentBrandingView | null {
  const value = useContext(SessionContext);
  if (value === null) return null;
  return value.session.status === "signed-in" ? value.session.tournament : null;
}

export function SessionProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  // Boot always starts here, because whether this device holds a credential is now an
  // asynchronous question. The effect below answers it before it asks the API anything.
  const [session, setSession] = useState<Session>({ status: "checking" });

  const signIn = useCallback(
    (identity: AppIdentityView, tournament: TournamentBrandingView | null): void => {
      setSession({ status: "signed-in", identity, tournament });
    },
    [],
  );

  const signOut = useCallback((): void => {
    clearCredential();
    setSession({ status: "signed-out", reason: "signed_out" });
  }, []);

  // Every authenticated 401, from any screen, lands here — including the boot check below — as
  // long as the credential it refused is still the one stored (`announceUnauthorised`, client).
  useEffect(
    () =>
      onUnauthorised(() => {
        clearCredential();
        setSession({ status: "signed-out", reason: "expired" });
      }),
    [],
  );

  useEffect(() => {
    if (session.status !== "checking") return undefined;
    let live = true;

    /**
     * Every write below goes through this rather than `setSession` directly, and the reason
     * is a real race: `/sign-in` renders inside this provider, so a link can be redeemed —
     * credential stored, `signIn` called — while this boot read is still in flight. A late
     * `signed-out` landing on top of that would sign out the device that had just signed in.
     * Boot may only move a session that is still booting.
     */
    const settle = (next: Session): void => {
      if (!live) return;
      setSession((current) => (current.status === "checking" ? next : current));
    };

    void (async () => {
      // Step one, and it must be first: the credential into the client's cache, so the call
      // below carries an `Authorization` header rather than going out bare.
      const credential = await loadCredential();
      if (!live) return;

      if (credential === null || credential === "") {
        settle({ status: "signed-out", reason: "no_credential" });
        return;
      }

      // RUN 87A: a credential is held and its owner is about to be checked. Said now, before the
      // network answers, so a sign-in link opened on this device is told it is already signed in.
      // Still `checking`, so this effect does not run again; and `settle` still gives way to a
      // redemption that has already moved the session on.
      settle({ status: "checking", credentialHeld: true });

      try {
        const body = await fetchIdentity();
        // `?? null` rather than the field alone: an older API build, or a test double that
        // mocks only what it cares about, returns a body with no `tournament` on it, and the
        // honest reading of an absent name is the same as of a null one — there is none.
        settle({ status: "signed-in", identity: body.identity, tournament: body.tournament ?? null });
      } catch (error: unknown) {
        // A 401 has already been handled by the subscriber above, which is the only place
        // allowed to throw a credential away — or, if a link replaced the credential while this
        // check was out, was about a credential nobody holds any more, and that redemption has
        // already moved the session on. Anything else is the network, not the person.
        if (error instanceof ApiError && error.status === 401) return;
        settle({
          status: "unreachable",
          error: error instanceof Error ? error : new Error("The sign-in check failed."),
        });
      }
    })();

    return () => {
      live = false;
    };
  }, [session.status]);

  const value = useMemo<SessionValue>(
    () => ({ session, signIn, signOut }),
    [session, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
